import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import dayjs from 'dayjs';

import { isPresent } from 'services/operators';
import { ApplicationConfigService } from 'services/application-config.service';
import { createRequestOption } from 'app/shared/util/request-util';
import { getThemeIdentifier, ITheme } from '../models/theme.model';

export type EntityResponseType = HttpResponse<ITheme>;
export type EntityArrayResponseType = HttpResponse<ITheme[]>;

@Injectable({ providedIn: 'root' })
export class ThemeService {
  protected resourceUrl: string;

  constructor(protected http: HttpClient, protected applicationConfigService: ApplicationConfigService) {
    this.resourceUrl = this.applicationConfigService.getEndpointFor('api/themes');
  }

  create(theme: ITheme): Observable<EntityResponseType> {
    const copy = this.convertDateFromClient(theme);
    return this.http.post<ITheme>(this.resourceUrl, copy, { observe: 'response' }).pipe(map((res: EntityResponseType) => this.convertDateFromServer(res)));
  }

  update(theme: ITheme): Observable<EntityResponseType> {
    const copy = this.convertDateFromClient(theme);
    return this.http
      .put<ITheme>(`${this.resourceUrl}/${getThemeIdentifier(theme)}`, copy, { observe: 'response' })
      .pipe(map((res: EntityResponseType) => this.convertDateFromServer(res)));
  }

  partialUpdate(theme: ITheme): Observable<EntityResponseType> {
    const copy = this.convertDateFromClient(theme);
    return this.http
      .patch<ITheme>(`${this.resourceUrl}/${getThemeIdentifier(theme)}`, copy, { observe: 'response' })
      .pipe(map((res: EntityResponseType) => this.convertDateFromServer(res)));
  }

  find(id: number): Observable<EntityResponseType> {
    return this.http.get<ITheme>(`${this.resourceUrl}/${id}`, { observe: 'response' }).pipe(map((res: EntityResponseType) => this.convertDateFromServer(res)));
  }

  query(req?: any): Observable<EntityArrayResponseType> {
    const options = createRequestOption(req);
    return this.http
      .get<ITheme[]>(this.resourceUrl, {
        params: options,
        observe: 'response',
      })
      .pipe(map((res: EntityArrayResponseType) => this.convertDateArrayFromServer(res)));
  }

  delete(id: number): Observable<HttpResponse<{}>> {
    return this.http.delete(`${this.resourceUrl}/${id}`, { observe: 'response' });
  }

  addThemeToCollectionIfMissing(themeCollection: ITheme[], ...themesToCheck: (ITheme | null | undefined)[]): ITheme[] {
    const themes: ITheme[] = themesToCheck.filter(isPresent);
    if (themes.length > 0) {
      const themeCollectionIdentifiers = themeCollection.map(themeItem => getThemeIdentifier(themeItem));
      const themesToAdd = themes.filter(themeItem => {
        const themeIdentifier = getThemeIdentifier(themeItem);
        if (themeIdentifier == null || themeCollectionIdentifiers.includes(themeIdentifier)) {
          return false;
        }
        themeCollectionIdentifiers.push(themeIdentifier);
        return true;
      });
      return [...themesToAdd, ...themeCollection];
    }
    return themeCollection;
  }

  protected convertDateFromClient(theme: ITheme): ITheme {
    return Object.assign({}, theme, {
      updatedDate: theme.updatedDate?.isValid() ? theme.updatedDate.toJSON() : undefined,
    });
  }

  protected convertDateFromServer(res: EntityResponseType): EntityResponseType {
    if (res.body) {
      res.body.updatedDate = res.body.updatedDate ? dayjs(res.body.updatedDate) : undefined;
    }
    return res;
  }

  protected convertDateArrayFromServer(res: EntityArrayResponseType): EntityArrayResponseType {
    if (res.body) {
      res.body.forEach((theme: ITheme) => {
        theme.updatedDate = theme.updatedDate ? dayjs(theme.updatedDate) : undefined;
      });
    }
    return res;
  }
}
